import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { employeesAPI, companiesAPI, leaveAPI, payrollAPI, documentsAPI, photoAPI, employeeProfileAPI, transferAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import TransferEmployeeDialog from '../components/TransferEmployeeDialog';
import EmployeeUserAccountCard from '../components/EmployeeUserAccountCard';
import ReportingManagerCard from '../components/ReportingManagerCard';
import FullEditEmployeeDialog from '../components/FullEditEmployeeDialog';
import EmployeeEditHistory from '../components/EmployeeEditHistory';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { 
  ArrowLeft, 
  Mail, 
  Building2, 
  Briefcase,
  Camera,
  Upload,
  FileText,
  CheckCircle2,
  XCircle,
  Trash2,
  Download,
  Calendar,
  ArrowRightLeft,
  TrendingUp,
  Activity,
  Award,
  Laptop,
  History,
  LogOut,
  UserCog,
} from 'lucide-react';

const DOC_CATEGORIES = [
  ['passport', 'Passport'], ['visa', 'Visa'], ['work_permit', 'Work Permit'],
  ['labor_contract', 'Labor Contract'], ['emirates_id', 'Emirates ID'], ['national_id', 'National ID'],
  ['offer_letter', 'Offer Letter'], ['appointment_letter', 'Appointment Letter'],
  ['educational_certificate', 'Educational Certificate'], ['experience_certificate', 'Experience Certificate'],
  ['medical_insurance', 'Medical Insurance'], ['bank_proof', 'Bank Proof'],
  ['tax_document', 'Tax Document'], ['other', 'Other'],
];

const EmployeeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const photoInputRef = useRef(null);
  const [employee, setEmployee] = useState(null);
  const [company, setCompany] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [docUploadCat, setDocUploadCat] = useState('passport');
  const [docExpiry, setDocExpiry] = useState('');
  const [docNumber, setDocNumber] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [liveDetails, setLiveDetails] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const canManage = ['super_admin', 'hr_admin'].includes(user?.role);
  const canTransfer = ['super_admin', 'hr_admin'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';
  const [fullEditOpen, setFullEditOpen] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);

  const fetchLiveDetails = async () => {
    setLiveLoading(true);
    try {
      const r = await employeeProfileAPI.details(id);
      setLiveDetails(r.data);
    } catch (e) { console.error(e); }
    finally { setLiveLoading(false); }
  };

  useEffect(() => {
    fetchEmployeeData();
    fetchLiveDetails();
    // eslint-disable-next-line
  }, [id]);

  const fetchEmployeeData = async () => {
    try {
      const empRes = await employeesAPI.getById(id);
      setEmployee(empRes.data);
      
      if (empRes.data.company_id) {
        const compRes = await companiesAPI.getById(empRes.data.company_id);
        setCompany(compRes.data);
      }
      
      try {
        const balanceRes = await leaveAPI.getBalance(id);
        setLeaveBalance(balanceRes.data);
      } catch (e) {
        console.log('No leave balance data');
      }
      
      try {
        const payslipsRes = await payrollAPI.getPayslips({ employee_id: id });
        setPayslips(payslipsRes.data);
      } catch (e) {
        console.log('No payslips data');
      }

      try {
        const docsRes = await documentsAPI.list(id);
        setDocuments(docsRes.data);
      } catch (e) {
        console.log('No documents data');
      }
    } catch (error) {
      toast.error('Failed to fetch employee data');
      navigate('/employees');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be under 5MB');
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await photoAPI.upload(id, fd);
      toast.success('Photo updated');
      fetchEmployeeData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', docUploadCat);
      if (docExpiry) fd.append('expiry_date', docExpiry);
      if (docNumber) fd.append('document_number', docNumber);
      await documentsAPI.upload(id, fd);
      toast.success('Document uploaded');
      setDocExpiry('');
      setDocNumber('');
      fetchEmployeeData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const verifyDoc = async (docId, decision) => {
    try {
      await documentsAPI.verify(docId, decision);
      toast.success(`Document ${decision}`);
      fetchEmployeeData();
    } catch (e) {
      toast.error('Verification failed');
    }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await documentsAPI.delete(docId);
      toast.success('Deleted');
      fetchEmployeeData();
    } catch { toast.error('Delete failed'); }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await documentsAPI.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name || 'document';
      link.click();
    } catch { toast.error('Download failed'); }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'on_leave': return 'warning';
      case 'terminated': return 'destructive';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!employee) return null;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/employees')}
          data-testid="back-btn"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to Employees
        </Button>
        <div className="flex flex-wrap gap-2">
          {isSuperAdmin && (
            <Button onClick={() => setFullEditOpen(true)} data-testid="full-edit-employee-btn">
              <UserCog size={16} className="mr-2" />Full Edit
            </Button>
          )}
          {canTransfer && employee.status === 'active' && (
            <Button variant="outline" onClick={() => setTransferOpen(true)} data-testid="transfer-employee-btn">
              <ArrowRightLeft size={16} className="mr-2" />Transfer to another company
            </Button>
          )}
        </div>
      </div>

      <FullEditEmployeeDialog
        open={fullEditOpen}
        onOpenChange={setFullEditOpen}
        employee={employee}
        onSuccess={() => { fetchEmployeeData(); fetchLiveDetails(); setHistoryKey(k => k + 1); }}
      />

      <TransferEmployeeDialog
        open={transferOpen}
        onOpenChange={setTransferOpen}
        employee={employee}
        onSuccess={() => { fetchEmployeeData(); fetchLiveDetails(); }}
      />

      {/* Employee Header */}
      <Card data-testid="employee-header">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="relative group">
              <Avatar className="h-24 w-24">
                {employee.photo_url ? <AvatarImage src={employee.photo_url} alt={`${employee.first_name}`} /> : null}
                <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                  {getInitials(employee.first_name, employee.last_name)}
                </AvatarFallback>
              </Avatar>
              {canManage && (
                <>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    className="hidden"
                    onChange={handlePhotoUpload}
                    data-testid="photo-upload-input"
                  />
                  <Button
                    size="icon"
                    variant="secondary"
                    className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full shadow"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    data-testid="upload-photo-btn"
                    title="Upload photo"
                  >
                    <Camera size={14} />
                  </Button>
                </>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold">
                  {employee.first_name} {employee.last_name}
                </h1>
                <Badge variant={getStatusBadgeVariant(employee.status)}>
                  {employee.status}
                </Badge>
              </div>
              <p className="text-muted-foreground mt-1">{employee.employee_code}</p>
              <div className="flex flex-wrap gap-4 mt-4">
                <div className="flex items-center text-sm text-muted-foreground">
                  <Briefcase size={16} className="mr-2" />
                  {employee.designation}
                </div>
                <div className="flex items-center text-sm text-muted-foreground">
                  <Building2 size={16} className="mr-2" />
                  {company?.name || 'N/A'}
                </div>
                <div className="flex items-center text-sm text-muted-foreground">
                  <Mail size={16} className="mr-2" />
                  {employee.email}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="live" data-testid="tab-live">Live Details</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents ({documents.length})</TabsTrigger>
          <TabsTrigger value="leaves" data-testid="tab-leaves">Leave Balance</TabsTrigger>
          <TabsTrigger value="payslips" data-testid="tab-payslips">Payslips</TabsTrigger>
          {canManage && <TabsTrigger value="changelog" data-testid="tab-changelog">Change Log</TabsTrigger>}
        </TabsList>

        <TabsContent value="details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <EmployeeUserAccountCard employee={employee} canManage={canManage} />
            </div>
            <div className="md:col-span-2">
              <ReportingManagerCard employee={employee} canManage={canManage} onChanged={fetchEmployeeData} />
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Full Name</p>
                    <p className="font-medium">{employee.first_name} {employee.last_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium break-all">{employee.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Personal Email</p>
                    <p className="font-medium break-all">{employee.personal_email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="font-medium">{employee.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Date of Birth</p>
                    <p className="font-medium">{employee.date_of_birth || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Nationality</p>
                    <p className="font-medium">{employee.nationality || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Gender</p>
                    <p className="font-medium capitalize">{employee.gender || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Marital Status</p>
                    <p className="font-medium capitalize">{employee.marital_status || 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Employment Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Employee Code</p>
                    <p className="font-medium">{employee.employee_code}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Join Date</p>
                    <p className="font-medium">{employee.join_date}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Department</p>
                    <p className="font-medium">{employee.department}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Designation</p>
                    <p className="font-medium">{employee.designation}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Contract Type</p>
                    <p className="font-medium capitalize">{employee.contract_type || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Employment Type</p>
                    <p className="font-medium capitalize">{employee.employment_type || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Branch / Location</p>
                    <p className="font-medium">{employee.branch_location || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Probation End</p>
                    <p className="font-medium">{employee.probation_end_date || 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Identity & Compliance */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><FileText size={18} />Identity &amp; Compliance Documents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-muted/40 rounded-lg">
                    <p className="text-xs text-muted-foreground">Passport No.</p>
                    <p className="font-medium">{employee.passport_number || '—'}</p>
                    {employee.passport_expiry && (
                      <p className={`text-xs mt-1 ${new Date(employee.passport_expiry) <= new Date(Date.now() + 60*86400000) ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>Exp: {employee.passport_expiry}</p>
                    )}
                  </div>
                  <div className="p-3 bg-muted/40 rounded-lg">
                    <p className="text-xs text-muted-foreground">National ID</p>
                    <p className="font-medium">{employee.national_id || '—'}</p>
                  </div>
                  <div className="p-3 bg-muted/40 rounded-lg">
                    <p className="text-xs text-muted-foreground">Visa No.</p>
                    <p className="font-medium">{employee.visa_number || '—'}</p>
                    {employee.visa_expiry && (
                      <p className={`text-xs mt-1 ${new Date(employee.visa_expiry) <= new Date(Date.now() + 60*86400000) ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>Exp: {employee.visa_expiry}</p>
                    )}
                  </div>
                  <div className="p-3 bg-muted/40 rounded-lg">
                    <p className="text-xs text-muted-foreground">Work Permit</p>
                    <p className="font-medium">{employee.work_permit_expiry ? 'Issued' : '—'}</p>
                    {employee.work_permit_expiry && (
                      <p className={`text-xs mt-1 ${new Date(employee.work_permit_expiry) <= new Date(Date.now() + 60*86400000) ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>Exp: {employee.work_permit_expiry}</p>
                    )}
                  </div>
                  <div className="p-3 bg-muted/40 rounded-lg">
                    <p className="text-xs text-muted-foreground">Labor Contract No.</p>
                    <p className="font-medium">{employee.labor_contract_number || '—'}</p>
                    {employee.labor_contract_expiry && (
                      <p className={`text-xs mt-1 ${new Date(employee.labor_contract_expiry) <= new Date(Date.now() + 60*86400000) ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>Exp: {employee.labor_contract_expiry}</p>
                    )}
                  </div>
                </div>
                {Object.keys(employee.country_compliance || {}).length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium mb-2">Country-Specific Compliance</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      {Object.entries(employee.country_compliance).map(([k, v]) => (
                        <div key={k}><span className="text-muted-foreground">{k.replace(/_/g, ' ')}:</span> <span className="font-medium break-all">{String(v) || '—'}</span></div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Emergency Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Emergency Contact</CardTitle>
              </CardHeader>
              <CardContent>
                {employee.emergency_contact && Object.values(employee.emergency_contact).some(Boolean) ? (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Name</p>
                      <p className="font-medium">{employee.emergency_contact.name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Relationship</p>
                      <p className="font-medium capitalize">{employee.emergency_contact.relationship || '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Phone</p>
                      <p className="font-medium">{employee.emergency_contact.phone || '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Alt. Phone</p>
                      <p className="font-medium">{employee.emergency_contact.alt_phone || '—'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Address</p>
                      <p className="font-medium">{employee.emergency_contact.address || '—'}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No emergency contact on record.</p>
                )}
              </CardContent>
            </Card>

            {/* Address */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Address</CardTitle>
              </CardHeader>
              <CardContent>
                {employee.address && Object.values(employee.address).some(Boolean) ? (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Street</p>
                      <p className="font-medium">{employee.address.street || '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">City</p>
                      <p className="font-medium">{employee.address.city || '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">State / Region</p>
                      <p className="font-medium">{employee.address.state || '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Country</p>
                      <p className="font-medium">{employee.address.country || '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Postal Code</p>
                      <p className="font-medium">{employee.address.postal_code || '—'}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No address on record.</p>
                )}
              </CardContent>
            </Card>

            {/* Bank Details (HR only) */}
            {canManage && (
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Bank Details</CardTitle>
                </CardHeader>
                <CardContent>
                  {employee.bank_details && Object.values(employee.bank_details).some(Boolean) ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><p className="text-muted-foreground">Bank Name</p><p className="font-medium">{employee.bank_details.bank_name || '—'}</p></div>
                      <div><p className="text-muted-foreground">Account Holder</p><p className="font-medium">{employee.bank_details.account_holder || '—'}</p></div>
                      <div><p className="text-muted-foreground">Account No.</p><p className="font-medium">{employee.bank_details.account_number || '—'}</p></div>
                      <div><p className="text-muted-foreground">IBAN</p><p className="font-medium break-all">{employee.bank_details.iban || '—'}</p></div>
                      <div><p className="text-muted-foreground">Swift / BIC</p><p className="font-medium">{employee.bank_details.swift_code || '—'}</p></div>
                      <div><p className="text-muted-foreground">Branch</p><p className="font-medium">{employee.bank_details.branch || '—'}</p></div>
                      <div><p className="text-muted-foreground">Currency</p><p className="font-medium">{employee.bank_details.currency || '—'}</p></div>
                      <div><p className="text-muted-foreground">Routing No.</p><p className="font-medium">{employee.bank_details.routing_number || '—'}</p></div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No bank details on record.</p>
                  )}
                </CardContent>
              </Card>
            )}

            {employee.salary_structure && (
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Salary Structure</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Basic Salary</p>
                      <p className="text-xl font-bold">
                        {employee.salary_structure.currency} {employee.salary_structure.basic?.toLocaleString()}
                      </p>
                    </div>
                    {Object.entries(employee.salary_structure.allowances || {}).map(([key, value]) => (
                      <div key={key} className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground capitalize">{key}</p>
                        <p className="text-xl font-bold">
                          {employee.salary_structure.currency} {value?.toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="live">
          {liveLoading || !liveDetails ? (
            <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Reporting */}
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Briefcase size={16} />Reporting</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div><span className="text-muted-foreground">Manager:</span> <span className="font-medium">{liveDetails.manager ? `${liveDetails.manager.name} (${liveDetails.manager.designation || ''})` : '—'}</span></div>
                  <div><span className="text-muted-foreground">Direct Reports:</span> <span className="font-medium">{liveDetails.direct_reports_count}</span></div>
                  {liveDetails.onboarding_status && (
                    <div><span className="text-muted-foreground">Onboarding:</span> <Badge variant={liveDetails.onboarding_status.status === 'approved' ? 'default' : 'secondary'}>{liveDetails.onboarding_status.status}</Badge></div>
                  )}
                </CardContent>
              </Card>

              {/* Attendance summary */}
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Activity size={16} />Attendance (Last 30 days)</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">Present</p><p className="text-xl font-bold text-emerald-600">{liveDetails.attendance_summary.present_days}</p></div>
                  <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">Late</p><p className="text-xl font-bold text-amber-600">{liveDetails.attendance_summary.late_days}</p></div>
                  <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">Absent</p><p className="text-xl font-bold text-red-600">{liveDetails.attendance_summary.absent_days}</p></div>
                  <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">Total Hours</p><p className="text-xl font-bold">{liveDetails.attendance_summary.total_hours}</p></div>
                </CardContent>
              </Card>

              {/* Leave summary */}
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar size={16} />Leaves ({liveDetails.leaves_summary.year})</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">Approved Days</p><p className="text-xl font-bold">{liveDetails.leaves_summary.approved_days}</p></div>
                  <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">Pending</p><p className="text-xl font-bold text-amber-600">{liveDetails.leaves_summary.pending}</p></div>
                  <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">Rejected</p><p className="text-xl font-bold text-red-600">{liveDetails.leaves_summary.rejected}</p></div>
                  <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">Total Requests</p><p className="text-xl font-bold">{liveDetails.leaves_summary.total_requests}</p></div>
                </CardContent>
              </Card>

              {/* Documents summary */}
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText size={16} />Documents</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">Total</p><p className="text-xl font-bold">{liveDetails.documents_summary.total}</p></div>
                  <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">Verified</p><p className="text-xl font-bold text-emerald-600">{liveDetails.documents_summary.verified}</p></div>
                  <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">Pending</p><p className="text-xl font-bold text-amber-600">{liveDetails.documents_summary.pending}</p></div>
                  <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">Expiring &lt;60 days</p><p className="text-xl font-bold text-red-600">{liveDetails.documents_summary.expiring_soon}</p></div>
                </CardContent>
              </Card>

              {/* Assets */}
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Laptop size={16} />Assigned Assets ({liveDetails.assets.length})</CardTitle></CardHeader>
                <CardContent className="space-y-1 text-sm max-h-48 overflow-y-auto">
                  {liveDetails.assets.length === 0 && <p className="text-muted-foreground italic">No assets assigned</p>}
                  {liveDetails.assets.map((a, i) => (
                    <div key={i} className="flex justify-between border-b pb-1">
                      <span><strong>{a.name}</strong> <span className="text-xs text-muted-foreground">({a.category})</span></span>
                      <span className="text-xs text-muted-foreground">{a.asset_tag}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Training */}
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Award size={16} />Training</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-3 gap-2 text-sm">
                  <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">Completed</p><p className="text-xl font-bold text-emerald-600">{liveDetails.training_summary.completed}</p></div>
                  <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">In Progress</p><p className="text-xl font-bold text-amber-600">{liveDetails.training_summary.in_progress}</p></div>
                  <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">Scheduled</p><p className="text-xl font-bold">{liveDetails.training_summary.scheduled}</p></div>
                </CardContent>
              </Card>

              {/* Performance */}
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp size={16} />Performance</CardTitle></CardHeader>
                <CardContent className="text-sm">
                  <div className="flex items-center gap-4">
                    <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">Reviews</p><p className="text-xl font-bold">{liveDetails.performance_summary.total_reviews}</p></div>
                    <div className="p-2 border rounded"><p className="text-xs text-muted-foreground">Average Rating</p><p className="text-xl font-bold text-amber-600">{liveDetails.performance_summary.average_rating}/5</p></div>
                  </div>
                </CardContent>
              </Card>

              {/* Salary history */}
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><History size={16} />Salary History</CardTitle></CardHeader>
                <CardContent className="space-y-1 text-sm max-h-48 overflow-y-auto">
                  {liveDetails.salary_history.length === 0 && <p className="text-muted-foreground italic">No revisions on record</p>}
                  {liveDetails.salary_history.map((s, i) => (
                    <div key={i} className="border-b pb-1">
                      <div className="flex justify-between"><span>{new Date(s.revised_at).toLocaleDateString()}</span><span className="font-medium">{s.old_currency || ''} {s.old_basic} → {s.new_currency || ''} {s.new_basic}</span></div>
                      <div className="text-xs text-muted-foreground">{s.reason}</div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Transfer history */}
              <Card className="lg:col-span-2">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><ArrowRightLeft size={16} />Inter-Company Transfer History</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {liveDetails.transfer_history.length === 0 && <p className="text-muted-foreground italic">No transfers on record</p>}
                  {liveDetails.transfer_history.map((t, i) => (
                    <div key={i} className="border rounded p-3 bg-muted/30">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline">{t.from_company_name} ({t.from_country})</Badge>
                        <ArrowRightLeft size={14} />
                        <Badge variant="default">{t.to_company_name} ({t.to_country})</Badge>
                        <span className="ml-auto text-xs text-muted-foreground">Effective {t.effective_date}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground">
                        <span>Designation: {t.from_designation} → {t.to_designation}</span>
                        <span>Salary: {t.from_currency} {t.from_basic} → {t.to_currency} {t.to_basic}</span>
                      </div>
                      <div className="text-xs mt-1">Reason: <strong>{t.reason}</strong></div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Exit / Offboarding */}
              {(liveDetails.exit_info || liveDetails.resignation || liveDetails.offboarding_summary) && (
                <Card className="lg:col-span-2 border-amber-200 dark:border-amber-900/50">
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><LogOut size={16} />Exit & Offboarding</CardTitle></CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {liveDetails.exit_info && (
                      <div className="grid grid-cols-2 gap-2 p-2 bg-red-50 dark:bg-red-950/30 rounded">
                        <div><span className="text-muted-foreground">Type:</span> <strong>{liveDetails.exit_info.exit_type}</strong></div>
                        <div><span className="text-muted-foreground">Last Working Day:</span> <strong>{liveDetails.exit_info.last_working_day}</strong></div>
                        <div><span className="text-muted-foreground">Reason:</span> {liveDetails.exit_info.exit_reason}</div>
                        <div><span className="text-muted-foreground">Rehirable:</span> {liveDetails.exit_info.is_eligible_for_rehire ? 'Yes' : 'No'}</div>
                      </div>
                    )}
                    {liveDetails.offboarding_summary && (
                      <div className="flex items-center gap-3 p-2 border rounded">
                        <span className="text-muted-foreground">Offboarding Progress:</span>
                        <strong>{liveDetails.offboarding_summary.progress.completed}/{liveDetails.offboarding_summary.progress.total}</strong>
                        <Badge variant={liveDetails.offboarding_summary.status === 'completed' ? 'default' : 'secondary'}>{liveDetails.offboarding_summary.status}</Badge>
                        {liveDetails.offboarding_summary.has_ff && <Badge variant="outline">F&amp;F Saved</Badge>}
                        {liveDetails.offboarding_summary.has_exit_interview && <Badge variant="outline">Interview Done</Badge>}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Employee Documents</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {canManage && (
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 p-3 border rounded-lg bg-muted/30">
                  <div className="sm:col-span-1">
                    <Label className="text-xs">Category</Label>
                    <Select value={docUploadCat} onValueChange={setDocUploadCat}>
                      <SelectTrigger data-testid="doc-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DOC_CATEGORIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Doc Number</Label>
                    <Input value={docNumber} onChange={(e) => setDocNumber(e.target.value)} placeholder="Optional" />
                  </div>
                  <div>
                    <Label className="text-xs">Expiry (optional)</Label>
                    <Input type="date" value={docExpiry} onChange={(e) => setDocExpiry(e.target.value)} />
                  </div>
                  <div className="flex items-end">
                    <label className="w-full">
                      <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleDocUpload} data-testid="doc-upload-input" />
                      <Button asChild disabled={uploadingDoc} className="w-full">
                        <span><Upload size={16} className="mr-2" />{uploadingDoc ? 'Uploading…' : 'Upload Doc'}</span>
                      </Button>
                    </label>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {documents.length === 0 && <p className="text-muted-foreground text-center py-6">No documents uploaded</p>}
                {documents.map(d => (
                  <div key={d.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`doc-${d.id}`}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileText size={20} className="text-muted-foreground shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{d.file_name}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-xs">{d.category}</Badge>
                          <Badge variant={d.status === 'verified' ? 'default' : d.status === 'rejected' ? 'destructive' : 'secondary'} className="text-xs">{d.status}</Badge>
                          {d.expiry_date && <span className="text-xs text-muted-foreground">Expires: {d.expiry_date}</span>}
                          {d.document_number && <span className="text-xs text-muted-foreground">#{d.document_number}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => downloadDoc(d)} title="Download"><Download size={14} /></Button>
                      {canManage && d.status !== 'verified' && (
                        <Button size="sm" variant="outline" onClick={() => verifyDoc(d.id, 'verify')} title="Verify"><CheckCircle2 size={14} /></Button>
                      )}
                      {canManage && d.status !== 'rejected' && (
                        <Button size="sm" variant="outline" onClick={() => verifyDoc(d.id, 'reject')} title="Reject"><XCircle size={14} /></Button>
                      )}
                      {canManage && (
                        <Button size="sm" variant="outline" onClick={() => deleteDoc(d.id)} title="Delete"><Trash2 size={14} /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leaves">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Leave Balance</CardTitle>
            </CardHeader>
            <CardContent>
              {leaveBalance.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {leaveBalance.map((leave, index) => (
                    <div key={index} className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar size={18} className="text-primary" />
                        <h4 className="font-medium">{leave.leave_type}</h4>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-2xl font-bold">{leave.total}</p>
                          <p className="text-xs text-muted-foreground">Total</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-amber-500">{leave.used}</p>
                          <p className="text-xs text-muted-foreground">Used</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-emerald-500">{leave.remaining}</p>
                          <p className="text-xs text-muted-foreground">Remaining</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No leave balance data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {canManage && (
          <TabsContent value="changelog">
            <EmployeeEditHistory employeeId={id} refreshKey={historyKey} />
          </TabsContent>
        )}

        <TabsContent value="payslips">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Payslips</CardTitle>
            </CardHeader>
            <CardContent>
              {payslips.length > 0 ? (
                <div className="space-y-3">
                  {payslips.map((payslip) => (
                    <div 
                      key={payslip.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <FileText className="text-primary" size={18} />
                        </div>
                        <div>
                          <p className="font-medium">
                            {new Date(payslip.year, payslip.month - 1).toLocaleDateString('en-US', { 
                              month: 'long', 
                              year: 'numeric' 
                            })}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Generated {new Date(payslip.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">
                          {payslip.currency} {payslip.net_salary?.toLocaleString()}
                        </p>
                        <p className="text-sm text-muted-foreground">Net Salary</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No payslips available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmployeeDetail;
